'use client'

import {
  Globe,
  Search,
  MessageSquare,
  FileJson,
  Link2,
  Bot,
  ArrowRight,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Zap,
  Database,
} from 'lucide-react'

const brandColors = {
  primary: '#008dcc',
  accent: '#FDC304',
  pink: '#F90B8A',
  purple: '#8b5cf6',
  green: '#10b981',
  red: '#ef4444',
}

export function DiscoveryChainDiagram() {
  const steps = [
    {
      icon: FileJson,
      label: 'ai-plugin.json',
      description: 'AI system discovers your capabilities',
      color: brandColors.purple,
    },
    {
      icon: Globe,
      label: 'llms.txt',
      description: 'Reads structured site overview',
      color: brandColors.primary,
    },
    {
      icon: MessageSquare,
      label: '/api/ask',
      description: 'Queries your knowledge directly',
      color: brandColors.accent,
    },
    {
      icon: Link2,
      label: 'Citation',
      description: 'Cites your content with URLs',
      color: brandColors.green,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-6 text-center'>
          The AI Discovery Chain
        </h3>
        <div className='space-y-4'>
          {steps.map((step, i) => (
            <div key={step.label}>
              <div className='flex items-start gap-4'>
                <div
                  className='flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center'
                  style={{
                    backgroundColor: `${step.color}15`,
                    color: step.color,
                  }}
                >
                  <step.icon className='w-6 h-6' />
                </div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span
                      className='text-xs font-mono px-2 py-0.5 rounded-full'
                      style={{
                        backgroundColor: `${step.color}15`,
                        color: step.color,
                      }}
                    >
                      Step {i + 1}
                    </span>
                  </div>
                  <p className='text-foreground font-medium mt-1'>
                    {step.label}
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    {step.description}
                  </p>
                </div>
              </div>
              {i < steps.length - 1 && (
                <div className='flex justify-center py-2'>
                  <ArrowRight className='w-4 h-4 text-muted-foreground rotate-90' />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function ScrapedVsCitedDiagram() {
  const rows = [
    {
      label: 'How AI gets your content',
      scraped: 'Parses raw HTML',
      cited: 'Structured API response',
      icon: Search,
    },
    {
      label: 'Content accuracy',
      scraped: 'AI paraphrases (or hallucinates)',
      cited: 'You control the answer',
      icon: MessageSquare,
    },
    {
      label: 'Attribution',
      scraped: 'Maybe a link, maybe not',
      cited: 'Source URLs in every response',
      icon: Link2,
    },
    {
      label: 'Discoverability',
      scraped: 'Depends on crawl luck',
      cited: 'Declared in ai-plugin.json',
      icon: Globe,
    },
    {
      label: 'Update speed',
      scraped: 'Waits for next crawl',
      cited: 'Real-time from knowledge graph',
      icon: Zap,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-6 text-center'>
          Scraped vs. Primary Source
        </h3>
        <div className='space-y-3'>
          {rows.map(row => (
            <div
              key={row.label}
              className='bg-background border border-border/60 rounded-xl p-4'
            >
              <div className='flex items-center gap-2 mb-3'>
                <row.icon className='w-4 h-4 text-muted-foreground' />
                <span className='text-sm font-medium text-foreground'>
                  {row.label}
                </span>
              </div>
              <div className='grid grid-cols-2 gap-3'>
                <div
                  className='rounded-lg px-3 py-2'
                  style={{
                    backgroundColor: `${brandColors.red}08`,
                    borderLeft: `3px solid ${brandColors.red}40`,
                  }}
                >
                  <div className='flex items-center gap-1.5 mb-1'>
                    <XCircle
                      className='w-3.5 h-3.5'
                      style={{ color: brandColors.red }}
                    />
                    <span
                      className='text-xs font-medium'
                      style={{ color: brandColors.red }}
                    >
                      Scraped
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground'>{row.scraped}</p>
                </div>
                <div
                  className='rounded-lg px-3 py-2'
                  style={{
                    backgroundColor: `${brandColors.green}08`,
                    borderLeft: `3px solid ${brandColors.green}40`,
                  }}
                >
                  <div className='flex items-center gap-1.5 mb-1'>
                    <CheckCircle2
                      className='w-3.5 h-3.5'
                      style={{ color: brandColors.green }}
                    />
                    <span
                      className='text-xs font-medium'
                      style={{ color: brandColors.green }}
                    >
                      Primary Source
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground'>{row.cited}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function FAQAutomationDiagram() {
  const steps = [
    {
      icon: Database,
      label: 'Contentlayer reads MDX',
      detail: '45 blog posts parsed',
      color: brandColors.primary,
    },
    {
      icon: HelpCircle,
      label: 'Script identifies gaps',
      detail: 'Skips posts with manual FAQPage',
      color: brandColors.purple,
    },
    {
      icon: Bot,
      label: 'GPT-4o-mini extracts Q&A',
      detail: '3-5 genuine questions per post',
      color: brandColors.accent,
    },
    {
      icon: FileJson,
      label: 'FAQPage schema injected',
      detail: 'Auto-rendered on every blog page',
      color: brandColors.green,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-6 text-center'>
          FAQ Schema Automation Pipeline
        </h3>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          {steps.map((step, i) => (
            <div
              key={step.label}
              className='bg-background border border-border/60 rounded-xl p-4'
            >
              <div className='flex items-center gap-3'>
                <div
                  className='w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0'
                  style={{
                    backgroundColor: `${step.color}15`,
                    color: step.color,
                  }}
                >
                  <step.icon className='w-5 h-5' />
                </div>
                <div>
                  <div className='flex items-center gap-2'>
                    <span
                      className='text-xs font-mono'
                      style={{ color: step.color }}
                    >
                      {i + 1}
                    </span>
                    <span className='text-sm font-medium text-foreground'>
                      {step.label}
                    </span>
                  </div>
                  <p className='text-xs text-muted-foreground mt-0.5'>
                    {step.detail}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function KnowledgeAPIResponseDiagram() {
  return (
    <div data-blog-layout='narrow'>
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6 md:p-8'>
        <h3 className='text-lg font-semibold text-foreground mb-4 text-center'>
          /api/ask Response Structure
        </h3>
        <div className='bg-background border border-border/60 rounded-xl p-4 font-mono text-xs'>
          <div className='space-y-1'>
            <span className='text-muted-foreground'>{'{'}</span>
            <div className='pl-4'>
              <span style={{ color: brandColors.primary }}>
                &quot;answer&quot;
              </span>
              <span className='text-muted-foreground'>: </span>
              <span style={{ color: brandColors.green }}>
                &quot;Vector is a 12-dimension AI lead...&quot;
              </span>
            </div>
            <div className='pl-4'>
              <span style={{ color: brandColors.primary }}>
                &quot;sources&quot;
              </span>
              <span className='text-muted-foreground'>: [</span>
            </div>
            <div className='pl-8'>
              <span className='text-muted-foreground'>{'{ '}</span>
              <span style={{ color: brandColors.purple }}>
                &quot;title&quot;
              </span>
              <span className='text-muted-foreground'>: &quot;...&quot;, </span>
              <span style={{ color: brandColors.purple }}>&quot;url&quot;</span>
              <span className='text-muted-foreground'>
                : &quot;https://...&quot;
              </span>
              <span className='text-muted-foreground'>{' }'}</span>
            </div>
            <div className='pl-4'>
              <span className='text-muted-foreground'>]</span>
            </div>
            <div className='pl-4'>
              <span style={{ color: brandColors.primary }}>
                &quot;relatedEntities&quot;
              </span>
              <span className='text-muted-foreground'>: [</span>
            </div>
            <div className='pl-8'>
              <span className='text-muted-foreground'>{'{ '}</span>
              <span style={{ color: brandColors.accent }}>
                &quot;name&quot;
              </span>
              <span className='text-muted-foreground'>
                : &quot;Vector&quot;,{' '}
              </span>
              <span style={{ color: brandColors.accent }}>
                &quot;type&quot;
              </span>
              <span className='text-muted-foreground'>
                : &quot;Product&quot;
              </span>
              <span className='text-muted-foreground'>{' }'}</span>
            </div>
            <div className='pl-4'>
              <span className='text-muted-foreground'>]</span>
            </div>
            <div className='pl-4'>
              <span style={{ color: brandColors.primary }}>
                &quot;confidence&quot;
              </span>
              <span className='text-muted-foreground'>: </span>
              <span style={{ color: brandColors.pink }}>0.95</span>
            </div>
            <span className='text-muted-foreground'>{'}'}</span>
          </div>
        </div>
        <p className='text-xs text-muted-foreground mt-3 text-center'>
          Every response includes citation URLs and entity context
        </p>
      </div>
    </div>
  )
}
